import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../lib/axios';
import type { CustomerHistoryData, CustomerNote } from '../types';

// API functions for customer history
export const customerHistoryApi = {
  /**
   * Fetch complete customer history including orders, sales, reservations, quotes, invoices, deliveries, loyalty, and notes
   */
  getHistory: async (customerId: string): Promise<CustomerHistoryData> => {
    const response = await axiosInstance.get<CustomerHistoryData>(
      `/users/customers/${customerId}/history`
    );
    return response.data;
  },

  /**
   * Add a note to a customer's profile
   */
  addNote: async (
    customerId: string,
    data: { note: string; isInternal: boolean }
  ): Promise<CustomerNote> => {
    const response = await axiosInstance.post<CustomerNote>(
      `/users/customers/${customerId}/notes`,
      data
    );
    return response.data;
  },

  /**
   * Delete a customer note
   */
  deleteNote: async (customerId: string, noteId: string): Promise<void> => {
    await axiosInstance.delete(`/users/customers/${customerId}/notes/${noteId}`);
  },
};

/**
 * Hook for fetching customer history data
 *
 * @param customerId - Customer user UUID
 * @returns TanStack Query result with customer history data
 *
 * @example
 * ```tsx
 * const { data: history, isLoading, error } = useCustomerHistory('customer-uuid');
 * ```
 */
export const useCustomerHistory = (customerId: string | undefined) => {
  return useQuery({
    queryKey: ['customer-history', customerId],
    queryFn: () => customerHistoryApi.getHistory(customerId!),
    enabled: !!customerId,
  });
};

/**
 * Hook for adding a note to a customer's profile
 *
 * Automatically invalidates the customer history query on success
 *
 * @returns TanStack Query mutation result
 *
 * @example
 * ```tsx
 * const addNoteMutation = useAddCustomerNote();
 *
 * const handleAddNote = async () => {
 *   await addNoteMutation.mutateAsync({
 *     customerId: 'customer-uuid',
 *     note: 'Customer requested callback',
 *     isInternal: true,
 *   });
 * };
 * ```
 */
export const useAddCustomerNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId,
      note,
      isInternal,
    }: {
      customerId: string;
      note: string;
      isInternal: boolean;
    }) => {
      return customerHistoryApi.addNote(customerId, { note, isInternal });
    },
    onSuccess: (_, { customerId }) => {
      // Invalidate customer history to refresh notes list
      queryClient.invalidateQueries({ queryKey: ['customer-history', customerId] });
    },
  });
};

/**
 * Hook for deleting a customer note
 *
 * Automatically invalidates the customer history query on success
 *
 * @returns TanStack Query mutation result
 *
 * @example
 * ```tsx
 * const deleteNoteMutation = useDeleteCustomerNote();
 *
 * const handleDeleteNote = async (noteId: string) => {
 *   await deleteNoteMutation.mutateAsync({
 *     customerId: 'customer-uuid',
 *     noteId,
 *   });
 * };
 * ```
 */
export const useDeleteCustomerNote = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ customerId, noteId }: { customerId: string; noteId: string }) => {
      return customerHistoryApi.deleteNote(customerId, noteId);
    },
    onSuccess: (_, { customerId }) => {
      // Invalidate customer history to refresh notes list
      queryClient.invalidateQueries({ queryKey: ['customer-history', customerId] });
    },
  });
};
